import { AlphaQuoteLineStrictCalculatedData } from '@snap-types/models/alpha-quote-line';
import { Document } from '@snap-types/models/document';
import {
  AlphaDimensionUnit,
  AlphaQuoteEvaluationStatus,
  AlphaQuoteLineStatus,
  AlphaQuoteSyncStatus,
} from '@snap-types/models/enums';
import { Nullish } from '@snap-types/types/nullish';

import type {
  BooleanValue,
  DecimalValue,
  DescriptionValue,
  NumberValue,
  SelectValue,
  TextValue,
} from './value';

export {
  AlphaQuoteEvaluationStatus,
  AlphaQuoteLineStatus,
  AlphaQuoteSyncStatus,
};

export type LineItemDisplayStatus =
  | 'calculating'
  | 'pending-delete'
  | 'apply-error'
  | 'calculation-error'
  | 'consult-engineering'
  | 'incomplete'
  | 'applied'
  | 'calculated-since-applied'
  | 'calculated'
  | 'customer-requires-sems';

export type LineItem = {
  uuid: string;
  dimensionUnit: Nullish<AlphaDimensionUnit>;
  quantityBreakUuids: string[];

  isPending: boolean;
  status: AlphaQuoteLineStatus;
  evaluationStatus: AlphaQuoteEvaluationStatus;
  syncStatus: AlphaQuoteSyncStatus;
  snapQuoteLineUuid: Nullish<string>;
  lastAppliedAt: Nullish<Date>;
  lastApplyFailedAt: Nullish<Date>;
  calculatedData: CalculatedLineItemData | null;
  attachments: LineItemAttachment[];

  lineNumber: NumberValue;

  description: DescriptionValue;
  descriptionIsOverridden: BooleanValue;

  drawingId: TextValue;
  drawingRevisionNo: TextValue;
  notes: TextValue;
  customerPartId: TextValue;

  fastenerType: SelectValue;
  material: SelectValue;
  dfars: BooleanValue;
  threadSize: SelectValue;
  threadPitch: SelectValue;
  length: DecimalValue;

  shoulderDiameter: DecimalValue;
  shoulderLength: DecimalValue;

  headStyle: SelectValue;
  recess: SelectValue;
  serration: BooleanValue;

  surfaceFinish: SelectValue;
  postFinish: SelectValue;

  sems1Style: SelectValue;
  sems1Weight: DecimalValue;
  sems2Style: SelectValue;
  sems2Weight: DecimalValue;

  osp2: SelectValue;
  osp2Description: TextValue;
  osp2UOM: SelectValue;
  osp2MinLot: DecimalValue;

  osp3: SelectValue;
  osp3Description: TextValue;
  osp3UOM: SelectValue;
  osp3MinLot: DecimalValue;

  toolingNote: TextValue;
  headingNote: TextValue;
  trimmingNote: TextValue;
  pinchNote: TextValue;
  rollingNote: TextValue;
  shankSlottingNote: TextValue;
  sawSlottingNote: TextValue;
  dephosNote: TextValue;
  heatTreatNote: TextValue;
  surfaceFinishNote: TextValue;
  postFinishNote: TextValue;
  osp2Note: TextValue;
  osp3Note: TextValue;

  hasSemsEmbed: BooleanValue;
};

export type CalculatedLineItemData = AlphaQuoteLineStrictCalculatedData;

export type LineItemAttachment = Pick<
  Document,
  'id' | 'fileName' | 'fileType' | 'createdAt'
>;

export type LineItemFieldKey = keyof Omit<
  LineItem,
  | 'uuid'
  | 'status'
  | 'evaluationStatus'
  | 'syncStatus'
  | 'snapQuoteLineUuid'
  | 'lastAppliedAt'
  | 'lastApplyFailedAt'
  | 'dimensionUnit'
  | 'calculatedData'
  | 'quantityBreakUuids'
  | 'isPending'
  | 'calculatedDescription'
  | 'attachments'
>;

export const lineItemFieldModel = 'lineItem';
export type LineItemFieldModel = typeof lineItemFieldModel;
